import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Helmet } from "react-helmet";
import { ShieldCheck, Cpu, Award, FileSearch, UserCheck } from "lucide-react";
import Header from "../components/Header";
import HeroVertical from "../components/HeroVertical";
import ReassuranceBar from "../components/ReassuranceBar";
import VerticalSections from "../components/VerticalSections";
import LeadFormPV from "../components/LeadFormPV";
import FAQVertical from "../components/FAQVertical";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsAppButton";
import MobileStickyBar from "../components/MobileStickyBar";
import ConfirmationScreen from "../components/ConfirmationScreen";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASSURANCE_ITEMS = [
  { icon: ShieldCheck, text: "Entreprise certifiée", description: "Wallonie" },
  { icon: Cpu, text: "Onduleur de marque", description: "Matériel de qualité" },
  { icon: Award, text: "Garantie", description: "Panneaux & installation" },
  { icon: FileSearch, text: "Étude gratuite", description: "Rentabilité personnalisée" },
  { icon: UserCheck, text: "Installateur direct", description: "Pas un comparateur" },
];

const WHY_ITEMS = [
  "Un seul interlocuteur, de l'étude à la pose.",
  "C'est notre technicien qui dimensionne votre installation — pas un centre d'appel qui revend votre demande.",
  "Pas 3 démarcheurs. Un installateur direct, transparent sur le prix.",
  "Matériel de marque (onduleur, panneaux), pose soignée, suivi de production.",
];

const BENEFITS = [
  "Réduisez fortement votre facture d'électricité.",
  "Batterie en option pour stocker le jour et consommer le soir.",
  "Installation compatible avec la tarification dynamique (structure IMPACT 2026).",
  "Étude de rentabilité personnalisée avant tout engagement.",
];

const STEPS = [
  "Vous demandez votre étude gratuite (2 minutes).",
  "On vous rappelle rapidement et on affine votre projet.",
  "Installation par nos équipes, avec suivi de production.",
];

const FAQ_ITEMS = [
  {
    question: "Quel retour sur investissement puis-je espérer ?",
    answer:
      "En Wallonie, une installation bien dimensionnée est généralement rentabilisée en 5 à 8 ans, selon votre consommation et votre profil. Nous réalisons une étude de rentabilité personnalisée avant tout engagement.",
  },
  {
    question: "Faut-il une batterie ?",
    answer:
      "Pas obligatoirement. La batterie est une option intéressante pour stocker l'électricité produite en journée et la consommer le soir, surtout avec la tarification dynamique. Nous évaluons sa pertinence dans votre étude gratuite.",
  },
  {
    question: "Quelles zones couvrez-vous ?",
    answer:
      "Nous intervenons dans le Hainaut, le Brabant wallon et dans toute la Wallonie.",
  },
];

const WHATSAPP_MESSAGE =
  "Bonjour, je souhaite une étude gratuite pour des panneaux photovoltaïques. Pouvez-vous me recontacter ?";

const PhotovoltaiquePage = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submittedName, setSubmittedName] = useState("");

  const handleFormSubmit = async (formData) => {
    try {
      await axios.post(`${API}/leads`, formData);

      if (window.dataLayer) {
        window.dataLayer.push({
          event: "lead_form_submit",
          form_type: "devis",
          vertical: "photovoltaique",
          project_type: formData.type_projet,
        });
      }

      setSubmittedName(formData.prenom);
      setFormSubmitted(true);

      if (window.dataLayer) {
        window.dataLayer.push({ event: "lead_confirmation_view", vertical: "photovoltaique" });
      }

      toast.success("Demande envoyée avec succès !");
    } catch (error) {
      console.error("Error submitting lead:", error);
      toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
      throw error;
    }
  };

  if (formSubmitted) {
    return (
      <>
        <Helmet>
          <title>Demande envoyée | LLDLS Smart Energy</title>
        </Helmet>
        <ConfirmationScreen prenom={submittedName} onBack={() => setFormSubmitted(false)} />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Panneaux Photovoltaïques en Wallonie — Installateur Direct | LLDLS</title>
        <meta
          name="description"
          content="Installation de panneaux photovoltaïques par LLDLS, installateur certifié en Wallonie. Réduisez votre facture d'électricité. Étude gratuite. Pas un comparateur."
        />
        <meta property="og:title" content="Panneaux Photovoltaïques en Wallonie — Installateur Direct | LLDLS" />
        <meta
          property="og:description"
          content="Installation de panneaux photovoltaïques par LLDLS, installateur certifié en Wallonie. Réduisez votre facture d'électricité. Étude gratuite."
        />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_lead-capture-roof/artifacts/vssoxae4_IMG_2625.jpeg" />
        <meta property="og:url" content="https://lldls-devis.com/photovoltaique" />
      </Helmet>
      <div className="min-h-screen bg-slate-50">
        <Header />
        <HeroVertical
          badge="Installateur photovoltaïque certifié en Wallonie"
          title={
            <>
              Passez au solaire avec LLDLS — installateur{" "}
              <span className="text-amber-500">photovoltaïque en Wallonie</span>
            </>
          }
          subtitle={
            <>
              Produisez votre propre électricité, réduisez votre facture et{" "}
              <span className="text-white font-medium">gagnez en indépendance.</span>
            </>
          }
          note="Installation par LLDLS, installateur direct certifié — pas un comparateur."
          ctaLabel="Recevoir mon étude gratuite"
          bgImage="https://images.unsplash.com/photo-1509391366360-2e959784a276?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          whatsappMessage={WHATSAPP_MESSAGE}
        />
        <ReassuranceBar items={REASSURANCE_ITEMS} />
        <VerticalSections
          whyItems={WHY_ITEMS}
          benefitsTitle="Ce que le photovoltaïque vous apporte :"
          benefits={BENEFITS}
          steps={STEPS}
        />
        <LeadFormPV onSubmit={handleFormSubmit} />
        <FAQVertical items={FAQ_ITEMS} />
        <Footer />
        <WhatsAppButton />
        <MobileStickyBar ctaLabel="Étude" showWhatsApp whatsappMessage={WHATSAPP_MESSAGE} />
      </div>
    </>
  );
};

export default PhotovoltaiquePage;
